import React, { useCallback, useEffect, useMemo } from 'react';
import {
  Control,
  useFieldArray,
  UseFormSetValue,
  useWatch
} from 'react-hook-form';
import { Box } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { IndicatorConfigInput } from '@server/data-feed/schemas/input.schema';
import { IndicatorType } from '@shared/types';
import { useTradingFormContext } from '../trading-form-context';
import {
  TradingFormState,
  TradingIndicatorConfig
} from '../trading-form.types';
import { indicatorDefaults } from '@client/widgets/chart/constants/indicator-defaults';
import { FormArrayItem } from '@client/foundation/form/form-array-item';
import { TradingFormField } from '../trading-form-field';
import { FormContainer } from '@client/foundation/layout/form-container';
import { RsiFields } from './indicator-fields/rsi-fields';
import { MaFields } from './indicator-fields/ma-fields';
import { MacdFields } from './indicator-fields/macd-fields';
import { OrderBlocksFields } from './indicator-fields/order-blocks-fields';
import { ZigZagFields } from './indicator-fields/zigzag-fields';
import {
  getIndicatorIndexedPath,
  IndicatorsPath
} from './indicator-config-form.utils';
import { useTemplates } from '@client/features/trading-templates/create-template/templates.hook';
import { Section } from '@client/foundation/layout/section';
import { sectionHeaderPanelStyle } from '@client/foundation/layout/styling';
import { MasterPatternFields } from './indicator-fields/master-pattern-fields';
import { MarketStructureFields } from './indicator-fields/market-structure-fields';
import { VwapFields } from './indicator-fields/vwap-fields';
import { LiquidationFields } from './indicator-fields/liquidation-fields';
import { AppIconButton } from '@client/foundation/design-system/components';

export type IndicatorConfigFormProps = {
  indicatorsPath: IndicatorsPath;
  timeframeIndex: number;
};

type IndicatorsArrayFormProps = {
  indicatorsPath: IndicatorsPath;
  control: Control<TradingFormState>;
  setValue: UseFormSetValue<TradingFormState>;
};

export const IndicatorsArrayForm: React.FC<IndicatorsArrayFormProps> = ({
  indicatorsPath,
  control,
  setValue
}) => {
  const { fields, append, remove } = useFieldArray({
    control,
    name: indicatorsPath
  });

  const watchedIndicatorConfig = useWatch({ control, name: indicatorsPath });
  const indicatorConfig = useMemo(
    () => watchedIndicatorConfig ?? [],
    [watchedIndicatorConfig]
  );

  // Populate fields with default values when indicator type is updated
  const populateWithDefaults = useCallback(() => {
    // if indicator has a type field only, it means the type has been selected
    const newIndicatorIndex = indicatorConfig.findIndex(
      (config) => config.type && Object.keys(config).length === 1
    );
    const newIndicator = indicatorConfig[newIndicatorIndex];

    if (newIndicator) {
      const defaultConfig = indicatorDefaults[newIndicator.type];

      // don't update value if the only default field is the 'type'
      if (Object.keys(defaultConfig).length > 1) {
        const path = getIndicatorIndexedPath(indicatorsPath, newIndicatorIndex);
        setValue(path, defaultConfig);
      }
    }
  }, [indicatorConfig, setValue, indicatorsPath]);

  useEffect(() => {
    populateWithDefaults();
  }, [populateWithDefaults]);

  // Helper function to render fields based on the indicator type.
  const renderIndicatorFields = (
    index: number,
    fieldState: IndicatorConfigInput
  ) => {
    const indicatorIndexedPath = getIndicatorIndexedPath(indicatorsPath, index);

    switch (fieldState.type) {
      case 'RSI':
        return <RsiFields path={indicatorIndexedPath} />;
      case 'MA':
        return <MaFields path={indicatorIndexedPath} />;
      case 'VWAP':
        return <VwapFields path={indicatorIndexedPath} />;
      case 'MACD':
        return <MacdFields path={indicatorIndexedPath} />;
      case 'ORDER_BLOCKS':
        return <OrderBlocksFields path={indicatorIndexedPath} />;
      case 'ZIG_ZAG':
        return <ZigZagFields path={indicatorIndexedPath} />;
      case 'MASTER_PATTERN':
        return <MasterPatternFields path={indicatorIndexedPath} />;
      case 'MARKET_STRUCTURE':
        return <MarketStructureFields path={indicatorIndexedPath} />;
      case 'LIQUIDATION':
        return <LiquidationFields path={indicatorIndexedPath} />;
      default:
        return null;
    }
  };

  return (
    <Box>
      {fields.map((field, index) => {
        // Use the watched value if available; fallback to the field from useFieldArray.
        const fieldState = (indicatorConfig[index] ||
          field) as TradingIndicatorConfig;

        return (
          <Box key={field.id} sx={{ position: 'relative' }}>
            <Section sx={sectionHeaderPanelStyle}>
              <FormArrayItem key={field.id} index={index} onRemove={remove}>
                <Box sx={{ flex: 1 }}>
                  <FormContainer>
                    {/* Dropdown to select the indicator type */}
                    <TradingFormField
                      type="autocomplete"
                      name={`${getIndicatorIndexedPath(indicatorsPath, index)}.type`}
                      label="Select indicator"
                      options={Object.values(IndicatorType)}
                      defaultValue={fieldState?.type}
                      onChange={(_, value) => {
                        if (typeof value !== 'string') return;
                        if (!Object.values(IndicatorType).includes(value as IndicatorType)) {
                          return;
                        }

                        const indicatorTypePath =
                          `${getIndicatorIndexedPath(indicatorsPath, index)}.type` as const;
                        setValue(indicatorTypePath, value as IndicatorType);
                      }}
                    />
                    {renderIndicatorFields(index, fieldState)}
                  </FormContainer>
                </Box>
              </FormArrayItem>
            </Section>
          </Box>
        );
      })}
      <AppIconButton
        tone="primary"
        title="Add Indicator"
        aria-label="Add Indicator"
        onClick={() => append({} as TradingIndicatorConfig)}
      >
        <AddIcon />
      </AppIconButton>
    </Box>
  );
};

export const IndicatorConfigForm: React.FC<IndicatorConfigFormProps> = ({
  indicatorsPath,
  timeframeIndex
}) => {
  const { control, setValue } = useTradingFormContext();

  useTemplates({ type: 'indicators', path: indicatorsPath, timeframeIndex });

  return (
    <IndicatorsArrayForm
      indicatorsPath={indicatorsPath}
      control={control}
      setValue={setValue}
    />
  );
};
